// Projects + ready alerts (FaceShot-ChopShop-web/routes/projects.js).
// The signed-in buyer's work from every studio, and the unseen
// "your render is ready" alerts the frame shows.
import { useSyncExternalStore } from 'react';
import { api } from '@/lib/api';

export type ProjectStudio = 'music' | 'music3d' | 'story' | 'ads' | 'animate' | 'storybook';
export type ProjectState = 'working' | 'needs_you' | 'ready' | 'failed' | 'cancelled';

export interface Project {
  id: string;
  studio: ProjectStudio;
  kind: 'job' | 'book';
  state: ProjectState;
  stage: string;
  progress: number | null;
  detail: { name?: string; title?: string; seconds?: number; scenes?: number };
  open: string | { book: string };
  created_at: string | null;
  updated_at: string | null;
  ready_at: string | null;
}

export interface ReadyAlert {
  id: string;
  ref_kind: 'job' | 'book';
  ref_id: string;
  studio: ProjectStudio;
  stage: string;
  state: 'ready' | 'needs_you' | 'failed';
  detail: Project['detail'];
  open: Project['open'];
  at: string;
  text: { en: string; es: string };
}

function authHeaders(): HeadersInit {
  const token = api.getToken();
  return token ? { Accept: 'application/json', Authorization: `Bearer ${token}` } : { Accept: 'application/json' };
}

export async function getProjects(): Promise<{ projects: Project[]; alerts: ReadyAlert[] }> {
  const r = await fetch('/api/web/projects', { headers: authHeaders(), cache: 'no-store' });
  if (!r.ok) throw new Error(`projects ${r.status}`);
  return r.json();
}

async function getAlerts(): Promise<ReadyAlert[]> {
  const r = await fetch('/api/web/projects/alerts', { headers: authHeaders(), cache: 'no-store' });
  if (!r.ok) throw new Error(`alerts ${r.status}`);
  return (await r.json()).alerts || [];
}

// Alerts marked seen on this page load: a poll already in flight when they
// were marked must not bring them back.
const seenHere = new Set<string>();

export async function markAlertsSeen(ids?: string[]): Promise<void> {
  (ids && ids.length ? ids : alertStore.get().map((a) => a.id)).forEach((id) => seenHere.add(id));
  await fetch('/api/web/projects/alerts/seen', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(ids && ids.length ? { ids } : {}),
  }).catch(() => undefined);
  alertStore.set(alertStore.get().filter((a) => !seenHere.has(a.id)));
}

/** A fresh private link to the buyer's own storybook (same kind the emails carry). */
export async function openBookPath(id: string): Promise<string> {
  const r = await fetch(`/api/web/projects/books/${encodeURIComponent(id)}/open`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  if (!r.ok) throw new Error(`book ${r.status}`);
  return (await r.json()).path as string;
}

// ---- one shared alert poll for the whole frame -------------------------------
// The tab badge and the ready notice read the same list; one request a minute
// while the tab is visible and someone is signed in.

const POLL_MS = 60_000;
let alerts: ReadyAlert[] = [];
const listeners = new Set<() => void>();
const alertStore = {
  get: () => alerts,
  set: (next: ReadyAlert[]) => {
    alerts = next.filter((a) => !seenHere.has(a.id));
    listeners.forEach((l) => l());
  },
};

let timer: ReturnType<typeof setInterval> | null = null;
let users = 0;

async function refresh() {
  if (!api.getToken() || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
  try {
    alertStore.set(await getAlerts());
  } catch {
    /* offline or signed out: keep what we have */
  }
}

/** Tests only: forget what this page load marked seen. */
export function resetAlertsForTests() {
  seenHere.clear();
  alertStore.set([]);
}

/** Push alerts the Projects page fetched, so the frame needn't ask again. */
export function primeAlerts(next: ReadyAlert[]) {
  alertStore.set(next);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Start polling while at least one caller wants alerts. */
export function startAlertPolling(): () => void {
  users += 1;
  if (users === 1) {
    void refresh();
    timer = setInterval(refresh, POLL_MS);
    document.addEventListener('visibilitychange', refresh);
  }
  return () => {
    users -= 1;
    if (users === 0) {
      if (timer) clearInterval(timer);
      timer = null;
      document.removeEventListener('visibilitychange', refresh);
    }
  };
}

export function useReadyAlerts(): ReadyAlert[] {
  return useSyncExternalStore(subscribe, alertStore.get, alertStore.get);
}

export function alertText(a: ReadyAlert, lang: string): string {
  return lang.toLowerCase().startsWith('es') ? a.text.es : a.text.en;
}
